import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import { toast } from "react-toastify";
import { FaPencilAlt, FaTrashAlt, FaPlus, FaSearch, FaUndo } from "react-icons/fa";
import {
  getAllCategories,
  createCategoria,
  updateCategoria,
  deleteCategoria,
  restoreCategoria,
} from "../features/categorie/categorieSlice";
import { DeleteConfirmModal } from "./shared";

const emptyForm = { name: "", parent: "" };
const COLLAPSED_HEIGHT = 500;

function CategorieManager() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { categorie, isLoading: categorieLoading } = useSelector((state) => state.categorie);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoriaToEdit, setCategoriaToEdit] = useState(null);
  const [categoriaToDelete, setCategoriaToDelete] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    if (categorie.length === 0 && !categorieLoading) {
      dispatch(getAllCategories());
    }
  }, [categorie.length, categorieLoading, dispatch]);

  const visibleCategorie = useMemo(() => categorie.filter((cat) => !cat.hiddenForUser), [categorie]);
  const hiddenDefaults = useMemo(() => categorie.filter((cat) => cat.hiddenForUser), [categorie]);

  const topLevel = useMemo(() => visibleCategorie.filter((cat) => !cat.parent), [visibleCategorie]);
  const childrenOf = (parentId) => visibleCategorie.filter((cat) => cat.parent === parentId);
  const isOwn = (cat) => !cat.isDefault && cat.user && String(cat.user) === String(user?._id);
  // Every visible category can be acted on: owned customs are edited/deleted
  // in place, defaults are forked/hidden (see the backend for the split).
  const canManage = (cat) => isOwn(cat) || cat.isDefault;

  const query = searchTerm.trim().toLowerCase();
  const matches = (cat) => cat.name.toLowerCase().includes(query);
  const filteredTopLevel = query
    ? topLevel.filter((cat) => matches(cat) || childrenOf(cat._id).some(matches))
    : topLevel;
  const filteredChildrenOf = (parentId, parentMatches) => {
    const children = childrenOf(parentId);
    if (!query || parentMatches) return children;
    return children.filter(matches);
  };

  const openCreate = () => {
    setCategoriaToEdit(null);
    setFormData(emptyForm);
    setShowFormModal(true);
  };

  const openEdit = (cat) => {
    setCategoriaToEdit(cat);
    setFormData({ name: cat.name, parent: cat.parent || "" });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setCategoriaToEdit(null);
    setFormData(emptyForm);
  };

  const onChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Insert a category name");
      return;
    }
    setIsSaving(true);
    try {
      if (categoriaToEdit) {
        const unchanged =
          formData.name.trim() === categoriaToEdit.name &&
          (formData.parent || null) === (categoriaToEdit.parent || null);
        if (unchanged) {
          closeFormModal();
          return;
        }
        const result = await dispatch(
          updateCategoria({
            id: categoriaToEdit._id,
            name: formData.name,
            parent: formData.parent || null,
          })
        ).unwrap();
        toast.success(
          result.forked
            ? `Created your personal copy of "${result.name}"`
            : "Categoria updated"
        );
      } else {
        await dispatch(
          createCategoria({ name: formData.name, parent: formData.parent || null })
        ).unwrap();
        toast.success("Categoria created");
      }
      closeFormModal();
    } catch (error) {
      toast.error(error?.message || error || "Error saving categoria");
    } finally {
      setIsSaving(false);
    }
  };

  const askDelete = (cat) => {
    setCategoriaToDelete(cat);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const result = await dispatch(deleteCategoria(categoriaToDelete._id)).unwrap();
      toast.success(result.hidden ? "Categoria hidden from your list" : "Categoria deleted");
    } catch (error) {
      toast.error(error?.message || error || "Error deleting categoria");
    } finally {
      setShowDeleteModal(false);
      setCategoriaToDelete(null);
    }
  };

  const handleRestore = async (cat) => {
    try {
      await dispatch(restoreCategoria(cat._id)).unwrap();
      toast.success(`"${cat.name}" restored`);
    } catch (error) {
      toast.error(error?.message || error || "Error restoring categoria");
    }
  };

  const renderRow = (cat, isChild = false) => (
    <div
      key={cat._id}
      className={`d-flex align-items-center justify-content-between p-3 rounded-3 mb-2 ${
        isChild ? "ms-4 bg-white border" : "bg-light"
      }`}
    >
      <div className="d-flex align-items-center">
        <span className="text-dark text-sm font-weight-bold">{cat.name}</span>
        {cat.isDefault && (
          <span className="badge bg-secondary ms-2" style={{ fontSize: "0.65rem" }}>
            Default
          </span>
        )}
      </div>
      {canManage(cat) && (
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-link text-dark p-1 m-0"
            onClick={() => openEdit(cat)}
            aria-label="Edit categoria"
          >
            <FaPencilAlt size={13} />
          </button>
          <button
            className="btn btn-link text-danger p-1 m-0"
            onClick={() => askDelete(cat)}
            aria-label="Delete categoria"
          >
            <FaTrashAlt size={13} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="card mb-5">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0">Categorie di spesa</h6>
          <button className="btn bg-gradient-dark btn-sm mb-0 d-flex align-items-center gap-1" onClick={openCreate}>
            <FaPlus size={11} />
            <span>Nuova categoria</span>
          </button>
        </div>

        <InputGroup className="mb-3">
          <InputGroup.Text className="bg-light border-end-0">
            <FaSearch size={12} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            className="border-start-0"
            placeholder="Cerca categoria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        {categorieLoading && categorie.length === 0 ? (
          <p className="text-secondary text-sm mb-0">Loading categorie...</p>
        ) : filteredTopLevel.length === 0 ? (
          <p className="text-secondary text-sm mb-0">
            {query ? "No categoria matches your search." : "No categorie yet, create the first one."}
          </p>
        ) : (
          <>
            <div
              style={{
                maxHeight: expanded ? "none" : COLLAPSED_HEIGHT,
                overflowY: expanded ? "visible" : "auto",
              }}
            >
              {filteredTopLevel.map((cat) => {
                const parentMatches = !query || matches(cat);
                const children = filteredChildrenOf(cat._id, parentMatches);
                return (
                  <div key={cat._id}>
                    {renderRow(cat)}
                    {children.map((child) => renderRow(child, true))}
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-1">
              <button
                type="button"
                className="btn btn-link btn-sm text-dark"
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? "Comprimi" : "Espandi"}
              </button>
            </div>
          </>
        )}

        {hiddenDefaults.length > 0 && (
          <div className="mt-2 pt-2 border-top">
            <button
              type="button"
              className="btn btn-link btn-sm text-secondary p-0"
              onClick={() => setShowHidden((prev) => !prev)}
            >
              {showHidden ? "Nascondi" : "Mostra"} categorie nascoste ({hiddenDefaults.length})
            </button>
            {showHidden && (
              <div className="mt-2">
                {hiddenDefaults.map((cat) => (
                  <div
                    key={cat._id}
                    className="d-flex align-items-center justify-content-between p-2 rounded-3 mb-1 bg-light"
                  >
                    <span className="text-secondary text-sm">{cat.name}</span>
                    <button
                      className="btn btn-link text-dark p-1 m-0 d-flex align-items-center gap-1"
                      onClick={() => handleRestore(cat)}
                    >
                      <FaUndo size={11} />
                      <span className="text-xs">Restore</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <b>{categoriaToEdit ? "Edit categoria" : "New categoria"}</b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {categoriaToEdit?.isDefault && (
            <div className="alert alert-light border text-secondary text-sm py-2 px-3 mb-3">
              This is a shared default category. Saving creates your own
              personal copy — the original stays available to everyone.
            </div>
          )}
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                placeholder="Category name"
                onChange={onChange}
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Parent category (optional)</Form.Label>
              <Form.Select name="parent" value={formData.parent} onChange={onChange}>
                <option value="">— None (top-level) —</option>
                {topLevel
                  .filter((cat) => !categoriaToEdit || cat._id !== categoriaToEdit._id)
                  .map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
            <div className="d-flex align-item-center justify-content-end text-center gap-3">
              <button type="button" className="btn btn-outline-dark" onClick={closeFormModal}>
                <p className="m-0">Cancel</p>
              </button>
              <button type="submit" className="btn bg-gradient-dark" disabled={isSaving}>
                <p className="m-0">Save</p>
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Modal */}
      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title={categoriaToDelete?.name}
        itemName={categoriaToDelete?.name}
        itemType={categoriaToDelete?.isDefault ? "categoria-default" : "item"}
        confirmLabel={categoriaToDelete?.isDefault ? "Hide" : "Delete"}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

export default CategorieManager;
